<template>
  <div class="produits-page">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-box"></i>
          Gestion des Produits
        </h1>
        <div class="header-actions">
          <button 
            class="btn btn-primary"
            @click="showAddModal = true"
          >
            <i class="fas fa-plus"></i>
            Nouveau Produit
          </button>
          <button 
            class="btn btn-secondary"
            @click="exportProduits"
            :disabled="loading"
          >
            <i class="fas fa-download"></i>
            Exporter
          </button>
          <button 
            class="btn btn-outline"
            @click="syncProduits"
            :disabled="syncing"
          >
            <i class="fas fa-sync" :class="{ 'fa-spin': syncing }"></i>
            Synchroniser
          </button>
        </div>
      </div>
    </div>

    <!-- Statistiques -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-box"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(produitsStore.stats.total) }}</div>
          <div class="stat-label">Total Produits</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(produitsStore.stats.active) }}</div>
          <div class="stat-label">Produits Actifs</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon warning">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(produitsStore.stats.lowStock) }}</div>
          <div class="stat-label">Stock Faible</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon categories">
          <i class="fas fa-tags"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ formatCount(produitsStore.stats.categories) }}</div>
          <div class="stat-label">Catégories</div>
        </div>
      </div>
    </div>

    <!-- Filtres -->
    <div class="filters-section">
      <div class="filters-row">
        <div class="filter-group">
          <label>Recherche</label>
          <div class="search-input">
            <i class="fas fa-search"></i>
            <input 
              type="text" 
              v-model="searchQuery"
              placeholder="Nom, référence, description..."
              @input="debouncedSearch"
            >
          </div>
        </div>
        <div class="filter-group">
          <label>Catégorie</label>
          <select v-model="filters.category" @change="applyFilters">
            <option value="">Toutes les catégories</option>
            <option v-for="cat in produitsStore.uniqueCategories" :key="cat" :value="cat">{{ getCategoryLabel(cat) }}</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Statut</label>
          <select v-model="filters.status" @change="applyFilters">
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="discontinued">Arrêté</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Stock</label>
          <select v-model="filters.stock" @change="applyFilters">
            <option value="">Tous les stocks</option>
            <option value="in_stock">En stock</option>
            <option value="low_stock">Stock faible</option>
            <option value="out_of_stock">Rupture</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Fournisseur</label>
          <select v-model="filters.supplier" @change="applyFilters">
            <option value="">Tous les fournisseurs</option>
            <option v-for="sup in produitsStore.uniqueSuppliers" :key="sup" :value="sup">{{ getSupplierLabel(sup) }}</option>
          </select>
        </div>
        <button 
          class="btn btn-outline"
          @click="clearFilters"
          v-if="hasActiveFilters"
        >
          <i class="fas fa-times"></i>
          Effacer
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Chargement des produits...</p>
    </div>

    <!-- Vue en grille/liste -->
    <div v-else class="produits-container">
      <div class="view-controls">
        <div class="view-toggle">
          <button 
            class="btn btn-sm" 
            :class="{ 'btn-primary': viewMode === 'grid', 'btn-outline': viewMode !== 'grid' }"
            @click="viewMode = 'grid'"
          >
            <i class="fas fa-th"></i>
            Grille
          </button>
          <button 
            class="btn btn-sm" 
            :class="{ 'btn-primary': viewMode === 'list', 'btn-outline': viewMode !== 'list' }"
            @click="viewMode = 'list'"
          >
            <i class="fas fa-list"></i>
            Liste
          </button>
        </div>
        <div class="sort-controls">
          <select v-model="sortBy" @change="applySorting">
            <option value="name">Nom</option>
            <option value="reference">Référence</option>
            <option value="price">Prix</option>
            <option value="stock">Stock</option>
            <option value="category">Catégorie</option>
            <option value="createdAt">Date création</option>
          </select>
          <button 
            class="btn btn-sm btn-outline"
            @click="toggleSortDirection"
          >
            <i class="fas" :class="sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'"></i>
          </button>
        </div>
      </div>

      <!-- Vue grille -->
      <div v-if="viewMode === 'grid'" class="produits-grid">
        <div 
          v-for="produit in filteredProduits" 
          :key="produit.id" 
          class="produit-card"
          @click="viewProduit(produit)"
        >
          <div class="produit-image">
            <img 
              v-if="produit.image" 
              :src="produit.image" 
              :alt="produit.name"
              @error="handleImageError"
            >
            <div v-else class="no-image">
              <i class="fas fa-image"></i>
            </div>
            <div class="produit-badges">
              <span v-if="produit.isNew" class="badge badge-new">Nouveau</span>
              <span v-if="produit.stock != null && produit.minStock != null && produit.stock <= produit.minStock" class="badge badge-warning">Stock faible</span>
              <span v-if="produit.stock === 0" class="badge badge-danger">Rupture</span>
            </div>
          </div>
          <div class="produit-content">
            <div class="produit-header">
              <h3 class="produit-name">{{ produit.name }}</h3>
              <span class="produit-reference">{{ produit.reference }}</span>
            </div>
            <div class="produit-category">
              <i class="fas fa-tag"></i>
              {{ getCategoryLabel(produit.category) }}
            </div>
            <div class="produit-description">
              {{ produit.description }}
            </div>
            <div class="produit-footer">
              <div class="produit-price">
                {{ formatCurrency(produit.price) }}
              </div>
              <div class="produit-stock">
                <i class="fas fa-boxes"></i>
                {{ formatCount(produit.stock) }} en stock
              </div>
            </div>
            <div class="produit-actions">
              <button 
                class="btn btn-sm btn-outline"
                @click.stop="editProduit(produit)"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                class="btn btn-sm btn-outline"
                @click.stop="duplicateProduit(produit)"
              >
                <i class="fas fa-copy"></i>
              </button>
              <button 
                class="btn btn-sm btn-outline btn-danger"
                @click.stop="confirmDelete(produit)"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Vue liste -->
      <div v-else class="produits-table-container" data-scroll-x>
        <table class="produits-table">
          <thead>
            <tr>
              <th>Image</th>
              <th @click="sortByField('name')" class="sortable">
                Nom
                <i class="fas fa-sort" :class="getSortIcon('name')"></i>
              </th>
              <th @click="sortByField('reference')" class="sortable">
                Référence
                <i class="fas fa-sort" :class="getSortIcon('reference')"></i>
              </th>
              <th>Catégorie</th>
              <th @click="sortByField('price')" class="sortable">
                Prix
                <i class="fas fa-sort" :class="getSortIcon('price')"></i>
              </th>
              <th @click="sortByField('stock')" class="sortable">
                Stock
                <i class="fas fa-sort" :class="getSortIcon('stock')"></i>
              </th>
              <th>Fournisseur</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="produit in filteredProduits" :key="produit.id">
              <td>
                <div class="produit-image-small">
                  <img 
                    v-if="produit.image" 
                    :src="produit.image" 
                    :alt="produit.name"
                    @error="handleImageError"
                  >
                  <div v-else class="no-image-small">
                    <i class="fas fa-image"></i>
                  </div>
                </div>
              </td>
              <td>
                <div class="produit-info">
                  <div class="produit-name">{{ produit.name }}</div>
                  <div class="produit-description-short">{{ truncateText(produit.description, 50) }}</div>
                </div>
              </td>
              <td>
                <span class="produit-reference">{{ produit.reference }}</span>
              </td>
              <td>
                <span class="category-badge" :class="produit.category">
                  {{ getCategoryLabel(produit.category) }}
                </span>
              </td>
              <td>
                <span class="produit-price">{{ formatCurrency(produit.price) }}</span>
              </td>
              <td>
                <div class="stock-info">
                  <span class="stock-value" :class="getStockClass(produit)">{{ formatCount(produit.stock) }}</span>
                  <div class="stock-bar">
                    <div 
                      class="stock-fill" 
                      :style="{ width: getStockPercentage(produit) + '%' }"
                      :class="getStockClass(produit)"
                    ></div>
                  </div>
                </div>
              </td>
              <td>
                <span class="supplier-name">{{ getSupplierLabel(produit.supplier) }}</span>
              </td>
              <td>
                <span class="status-badge" :class="produit.status">
                  {{ getStatusLabel(produit.status) }}
                </span>
              </td>
              <td>
                <div class="actions-menu">
                  <button 
                    class="btn btn-sm btn-outline"
                    @click="viewProduit(produit)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    class="btn btn-sm btn-outline"
                    @click="editProduit(produit)"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="btn btn-sm btn-outline"
                    @click="duplicateProduit(produit)"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                  <button 
                    class="btn btn-sm btn-outline btn-danger"
                    @click="confirmDelete(produit)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container" v-if="produitsStore.pagination.total > produitsStore.pagination.limit">
        <div class="pagination-info">
          Affichage de {{ (produitsStore.pagination.page - 1) * produitsStore.pagination.limit + 1 }} à 
          {{ Math.min(produitsStore.pagination.page * produitsStore.pagination.limit, produitsStore.pagination.total) }} 
          sur {{ produitsStore.pagination.total }} produits
        </div>
        <div class="pagination-controls">
          <button 
            class="btn btn-sm btn-outline"
            @click="changePage(produitsStore.pagination.page - 1)"
            :disabled="produitsStore.pagination.page === 1"
          >
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="page-info">
            Page {{ produitsStore.pagination.page }} sur {{ totalPages }}
          </span>
          <button 
            class="btn btn-sm btn-outline"
            @click="changePage(produitsStore.pagination.page + 1)"
            :disabled="produitsStore.pagination.page === totalPages"
          >
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Ajout/Modification -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h3>{{ showAddModal ? 'Nouveau Produit' : 'Modifier Produit' }}</h3>
          <button class="modal-close" @click="closeModals">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveProduit">
            <div class="form-row">
              <div class="form-group">
                <label>Nom du produit *</label>
                <input 
                  type="text" 
                  v-model="produitForm.name" 
                  required
                  placeholder="Ex: MacBook Air 13 pouces"
                >
              </div>
              <div class="form-group">
                <label>Référence *</label>
                <input 
                  type="text" 
                  v-model="produitForm.reference" 
                  required
                  placeholder="Ex: MBA13-M2-256"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Catégorie *</label>
                <select v-model="produitForm.category" required>
                  <option value="informatique">Informatique</option>
                  <option value="bureautique">Bureautique</option>
                  <option value="mobilier">Mobilier</option>
                  <option value="accessoires">Accessoires</option>
                  <option value="consommables">Consommables</option>
                </select>
              </div>
              <div class="form-group">
                <label>Fournisseur</label>
                <select v-model="produitForm.supplier">
                  <option value="">Sélectionner un fournisseur</option>
                  <option value="dell">Dell</option>
                  <option value="hp">HP</option>
                  <option value="lenovo">Lenovo</option>
                  <option value="apple">Apple</option>
                  <option value="microsoft">Microsoft</option>
                  <option value="other">Autre</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>Description</label>
              <textarea 
                v-model="produitForm.description" 
                rows="3"
                placeholder="Description détaillée du produit..."
              ></textarea>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Prix unitaire (€) *</label>
                <input 
                  type="number" 
                  v-model.number="produitForm.price" 
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                >
              </div>
              <div class="form-group">
                <label>Prix d'achat (€)</label>
                <input 
                  type="number" 
                  v-model.number="produitForm.costPrice" 
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Stock actuel *</label>
                <input 
                  type="number" 
                  v-model.number="produitForm.stock" 
                  required
                  min="0"
                  placeholder="0"
                >
              </div>
              <div class="form-group">
                <label>Stock minimum</label>
                <input 
                  type="number" 
                  v-model.number="produitForm.minStock" 
                  min="0"
                  placeholder="5"
                >
              </div>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Unité</label>
                <select v-model="produitForm.unit">
                  <option value="piece">Pièce</option>
                  <option value="kg">Kilogramme</option>
                  <option value="liter">Litre</option>
                  <option value="meter">Mètre</option>
                  <option value="pack">Pack</option>
                </select>
              </div>
              <div class="form-group">
                <label>Statut</label>
                <select v-model="produitForm.status">
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                  <option value="discontinued">Arrêté</option>
                </select>
              </div>
            </div>
            
            <div class="form-group">
              <label>URL de l'image</label>
              <input 
                type="url" 
                v-model="produitForm.image" 
                placeholder="https://exemple.com/image.jpg"
              >
            </div>
            
            <div class="form-group">
              <label>Spécifications techniques</label>
              <textarea 
                v-model="produitForm.specifications" 
                rows="4"
                placeholder="Spécifications techniques détaillées..."
              ></textarea>
            </div>
            
            <div class="form-group">
              <label>Notes internes</label>
              <textarea 
                v-model="produitForm.notes" 
                rows="2"
                placeholder="Notes internes, commentaires..."
              ></textarea>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModals">
            Annuler
          </button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="saveProduit"
            :disabled="saving"
          >
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            {{ saving ? 'Enregistrement...' : (showAddModal ? 'Créer' : 'Modifier') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Détails -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content modal-large" @click.stop>
        <div class="modal-header">
          <h3>Détails du Produit</h3>
          <button class="modal-close" @click="closeModals">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body" v-if="selectedProduit">
          <div class="produit-details-grid">
            <div class="produit-image-large">
              <img 
                v-if="selectedProduit.image" 
                :src="selectedProduit.image" 
                :alt="selectedProduit.name"
                @error="handleImageError"
              >
              <div v-else class="no-image-large">
                <i class="fas fa-image"></i>
                <p>Aucune image</p>
              </div>
            </div>
            
            <div class="produit-info-details">
              <h2>{{ selectedProduit.name }}</h2>
              <p class="produit-reference">Référence: {{ selectedProduit.reference }}</p>
              
              <div class="detail-section">
                <h4>Informations générales</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <label>Catégorie:</label>
                    <span>{{ getCategoryLabel(selectedProduit.category) }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Fournisseur:</label>
                    <span>{{ getSupplierLabel(selectedProduit.supplier) }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Statut:</label>
                    <span class="status-badge" :class="selectedProduit.status">
                      {{ getStatusLabel(selectedProduit.status) }}
                    </span>
                  </div>
                  <div class="detail-item">
                    <label>Unité:</label>
                    <span>{{ getUnitLabel(selectedProduit.unit) }}</span>
                  </div>
                </div>
              </div>
              
              <div class="detail-section">
                <h4>Prix et stock</h4>
                <div class="detail-grid">
                  <div class="detail-item">
                    <label>Prix de vente:</label>
                    <span class="price-value">{{ formatCurrency(selectedProduit.price) }}</span>
                  </div>
                  <div class="detail-item" v-if="selectedProduit.costPrice">
                    <label>Prix d'achat:</label>
                    <span>{{ formatCurrency(selectedProduit.costPrice) }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Marge:</label>
                    <span class="margin-value">{{ calculateMargin(selectedProduit) }}</span>
                  </div>
                  <div class="detail-item">
                    <label>Stock actuel:</label>
                    <span class="stock-value" :class="getStockClass(selectedProduit)">
                      {{ formatCount(selectedProduit.stock) }}
                    </span>
                  </div>
                  <div class="detail-item">
                    <label>Stock minimum:</label>
                    <span>{{ formatCount(selectedProduit.minStock) }}</span>
                  </div>
                </div>
              </div>
              
              <div class="detail-section" v-if="selectedProduit.description">
                <h4>Description</h4>
                <p>{{ selectedProduit.description }}</p>
              </div>
              
              <div class="detail-section" v-if="selectedProduit.specifications">
                <h4>Spécifications techniques</h4>
                <pre class="specifications">{{ selectedProduit.specifications }}</pre>
              </div>
              
              <div class="detail-section" v-if="selectedProduit.notes">
                <h4>Notes internes</h4>
                <p>{{ selectedProduit.notes }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModals">
            Fermer
          </button>
          <button class="btn btn-primary" @click="editProduit(selectedProduit)">
            <i class="fas fa-edit"></i>
            Modifier
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Confirmation Suppression -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeModals">
      <div class="modal-content modal-small" @click.stop>
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="modal-close" @click="closeModals">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer le produit <strong>{{ produitToDelete?.name }}</strong> ?</p>
          <p class="text-warning">Cette action est irréversible.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeModals">
            Annuler
          </button>
          <button 
            class="btn btn-danger" 
            @click="deleteProduit"
            :disabled="deleting"
          >
            <i v-if="deleting" class="fas fa-spinner fa-spin"></i>
            {{ deleting ? 'Suppression...' : 'Supprimer' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useProduitsStore } from '@/stores/produits'
import { debounce } from 'lodash'

export default {
  name: 'Produits',
  setup() {
    const produitsStore = useProduitsStore()
    
    // État local
    const loading = ref(false)
    const saving = ref(false)
    const deleting = ref(false)
    const syncing = ref(false)
    const searchQuery = ref('')
    const viewMode = ref('grid')
    const sortBy = ref('name')
    const sortDirection = ref('asc')
    
    // Modals
    const showAddModal = ref(false)
    const showEditModal = ref(false)
    const showDetailsModal = ref(false)
    const showDeleteModal = ref(false)
    
    // Données sélectionnées
    const selectedProduit = ref(null)
    const produitToDelete = ref(null)
    
    // Filtres
    const filters = ref({
      category: '',
      status: '',
      stock: '',
      supplier: ''
    })
    
    // Formulaire produit
    const produitForm = ref({
      name: '',
      reference: '',
      description: '',
      category: 'informatique',
      supplier: '',
      price: 0,
      costPrice: 0,
      stock: 0,
      minStock: 5,
      unit: 'piece',
      status: 'active',
      image: '',
      specifications: '',
      notes: ''
    })
    
    // Computed
    const filteredProduits = computed(() => {
      let produits = [...produitsStore.produits]
      
      // Recherche textuelle
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        produits = produits.filter(produit => 
          (produit.name || '').toLowerCase().includes(query) ||
          (produit.reference || '').toLowerCase().includes(query) ||
          (produit.codeOracle || '').toLowerCase().includes(query) ||
          (produit.description || '').toLowerCase().includes(query)
        )
      }
      
      // Filtres
      if (filters.value.category) {
        produits = produits.filter(p => p.category === filters.value.category)
      }
      if (filters.value.status) {
        produits = produits.filter(p => p.status === filters.value.status)
      }
      if (filters.value.supplier) {
        produits = produits.filter(p => p.supplier === filters.value.supplier)
      }
      if (filters.value.stock) {
        switch (filters.value.stock) {
          case 'in_stock':
            produits = produits.filter(p => p.stock > p.minStock)
            break
          case 'low_stock':
            produits = produits.filter(p => p.stock <= p.minStock && p.stock > 0)
            break
          case 'out_of_stock':
            produits = produits.filter(p => p.stock === 0)
            break
        }
      }
      
      // Tri
      produits.sort((a, b) => {
        let aVal = a[sortBy.value]
        let bVal = b[sortBy.value]
        
        // Valeurs absentes (champ sans source API) toujours en fin de liste
        const aMissing = aVal === null || aVal === undefined
        const bMissing = bVal === null || bVal === undefined
        if (aMissing || bMissing) return aMissing === bMissing ? 0 : (aMissing ? 1 : -1)
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase()
        if (typeof bVal === 'string') bVal = bVal.toLowerCase()
        
        if (sortDirection.value === 'asc') {
          return aVal > bVal ? 1 : -1
        } else {
          return aVal < bVal ? 1 : -1
        }
      })
      
      return produits
    })
    
    const hasActiveFilters = computed(() => {
      return filters.value.category || filters.value.status || filters.value.stock || 
             filters.value.supplier || searchQuery.value
    })
    
    const totalPages = computed(() => {
      return Math.ceil(produitsStore.pagination.total / produitsStore.pagination.limit)
    })
    
    // Méthodes
    const loadProduits = async () => {
      loading.value = true
      try {
        await produitsStore.loadProduits()
      } finally {
        loading.value = false
      }
    }
    
    const debouncedSearch = debounce(() => {
      // La recherche est gérée par le computed filteredProduits
    }, 300)
    
    const sortByField = (field) => {
      if (sortBy.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortBy.value = field
        sortDirection.value = 'asc'
      }
    }
    
    const getSortIcon = (field) => {
      if (sortBy.value !== field) return ''
      return sortDirection.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }
    
    const toggleSortDirection = () => {
      sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    }
    
    const applySorting = () => {
      // Le tri est appliqué automatiquement via le computed
    }
    
    const applyFilters = () => {
      // Les filtres sont appliqués automatiquement via le computed
    }
    
    const clearFilters = () => {
      filters.value = {
        category: '',
        status: '',
        stock: '',
        supplier: ''
      }
      searchQuery.value = ''
    }
    
    const changePage = (page) => {
      produitsStore.setPagination(page)
      loadProduits()
    }
    
    const viewProduit = (produit) => {
      selectedProduit.value = produit
      showDetailsModal.value = true
    }
    
    const editProduit = (produit) => {
      produitForm.value = { ...produit }
      selectedProduit.value = produit
      showEditModal.value = true
      showDetailsModal.value = false
    }
    
    const duplicateProduit = (produit) => {
      produitForm.value = {
        ...produit,
        name: `${produit.name} (Copie)`,
        reference: `${produit.reference}-COPY`
      }
      showAddModal.value = true
    }
    
    const confirmDelete = (produit) => {
      produitToDelete.value = produit
      showDeleteModal.value = true
    }
    
    const saveProduit = async () => {
      saving.value = true
      try {
        if (showAddModal.value) {
          await produitsStore.createProduit(produitForm.value)
        } else {
          await produitsStore.updateProduit(selectedProduit.value.id, produitForm.value)
        }
        closeModals()
        await loadProduits()
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        saving.value = false
      }
    }
    
    const deleteProduit = async () => {
      deleting.value = true
      try {
        await produitsStore.deleteProduit(produitToDelete.value.id)
        closeModals()
        await loadProduits()
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
      } finally {
        deleting.value = false
      }
    }
    
    const syncProduits = async () => {
      syncing.value = true
      try {
        await produitsStore.syncProduits()
        await loadProduits()
      } catch (error) {
        console.error('Erreur lors de la synchronisation:', error)
      } finally {
        syncing.value = false
      }
    }
    
    const exportProduits = async () => {
      try {
        await produitsStore.exportProduits('csv', filters.value)
      } catch (error) {
        console.error('Erreur lors de l\'export:', error)
      }
    }
    
    const closeModals = () => {
      showAddModal.value = false
      showEditModal.value = false
      showDetailsModal.value = false
      showDeleteModal.value = false
      selectedProduit.value = null
      produitToDelete.value = null
      
      // Reset form
      produitForm.value = {
        name: '',
        reference: '',
        description: '',
        category: 'informatique',
        supplier: '',
        price: 0,
        costPrice: 0,
        stock: 0,
        minStock: 5,
        unit: 'piece',
        status: 'active',
        image: '',
        specifications: '',
        notes: ''
      }
    }
    
    // Utilitaires
    // « — » quand la valeur n'existe pas dans l'API (jamais de NaN / vide)
    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined || amount === '') return '—'
      const value = Number(amount)
      if (!Number.isFinite(value)) return '—'
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR'
      }).format(value)
    }
    
    const formatCount = (value) => {
      if (value === null || value === undefined || value === '') return '—'
      const n = Number(value)
      return Number.isFinite(n) ? n.toLocaleString('fr-FR') : '—'
    }
    
    const getCategoryLabel = (category) => {
      const labels = {
        informatique: 'Informatique',
        bureautique: 'Bureautique',
        mobilier: 'Mobilier',
        accessoires: 'Accessoires',
        consommables: 'Consommables'
      }
      return labels[category] || category || '—'
    }
    
    const getStatusLabel = (status) => {
      const labels = {
        active: 'Actif',
        inactive: 'Inactif',
        discontinued: 'Arrêté'
      }
      if (!status) return '—'
      return labels[status] || status
    }
    
    const getSupplierLabel = (supplier) => {
      const labels = {
        dell: 'Dell',
        hp: 'HP',
        lenovo: 'Lenovo',
        apple: 'Apple',
        microsoft: 'Microsoft',
        other: 'Autre'
      }
      return labels[supplier] || supplier || 'Non défini'
    }
    
    const getUnitLabel = (unit) => {
      const labels = {
        piece: 'Pièce',
        kg: 'Kilogramme',
        liter: 'Litre',
        meter: 'Mètre',
        pack: 'Pack'
      }
      return labels[unit] || unit || '—'
    }
    
    const getStockClass = (produit) => {
      if (produit.stock === null || produit.stock === undefined) return ''
      if (produit.stock === 0) return 'out-of-stock'
      if (produit.stock <= produit.minStock) return 'low-stock'
      return 'in-stock'
    }
    
    const getStockPercentage = (produit) => {
      if (produit.stock === null || produit.stock === undefined) return 0
      const maxStock = Math.max(produit.minStock * 3, 100)
      return Math.min((produit.stock / maxStock) * 100, 100)
    }
    
    const calculateMargin = (produit) => {
      if (!produit.costPrice || !produit.price) return '—'
      return Math.round(((produit.price - produit.costPrice) / produit.price) * 100) + '%'
    }
    
    const truncateText = (text, length) => {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    }
    
    const handleImageError = (event) => {
      event.target.style.display = 'none'
      event.target.nextElementSibling.style.display = 'flex'
    }
    
    // Lifecycle
    onMounted(() => {
      loadProduits()
    })
    
    return {
      produitsStore,
      loading,
      saving,
      deleting,
      syncing,
      searchQuery,
      viewMode,
      sortBy,
      sortDirection,
      showAddModal,
      showEditModal,
      showDetailsModal,
      showDeleteModal,
      selectedProduit,
      produitToDelete,
      filters,
      produitForm,
      filteredProduits,
      hasActiveFilters,
      totalPages,
      loadProduits,
      debouncedSearch,
      sortByField,
      getSortIcon,
      toggleSortDirection,
      applySorting,
      applyFilters,
      clearFilters,
      changePage,
      viewProduit,
      editProduit,
      duplicateProduit,
      confirmDelete,
      saveProduit,
      deleteProduit,
      syncProduits,
      exportProduits,
      closeModals,
      formatCurrency,
      formatCount,
      getCategoryLabel,
      getStatusLabel,
      getSupplierLabel,
      getUnitLabel,
      getStockClass,
      getStockPercentage,
      calculateMargin,
      truncateText,
      handleImageError
    }
  }
}
</script>

<style scoped>
.produits-page {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  background: var(--primary-color);
}

.stat-icon.active {
  background: var(--success-color);
}

.stat-icon.warning {
  background: var(--warning-color);
}

.stat-icon.categories {
  background: var(--info-color);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.filters-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.filters-row {
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 150px;
}

.filter-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.search-input {
  position: relative;
}

.search-input i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
}

.search-input input {
  padding-left: 2.5rem;
  width: 250px;
}

.view-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.view-toggle {
  display: flex;
  gap: 0.25rem;
}

.sort-controls {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.produits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.produit-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.produit-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.produit-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.produit-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 2rem;
}

.produit-badges {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
}

.badge-new {
  background: var(--success-color);
}

.badge-warning {
  background: var(--warning-color);
}

.badge-danger {
  background: var(--danger-color);
}

.produit-content {
  padding: 1rem;
}

.produit-header {
  margin-bottom: 0.5rem;
}

.produit-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.produit-reference {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: monospace;
}

.produit-category {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
}

.produit-description {
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.4;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.produit-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.produit-price {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
}

.produit-stock {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.produit-actions {
  display: flex;
  gap: 0.25rem;
  justify-content: flex-end;
}

.produits-table-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.produits-table {
  width: 100%;
  border-collapse: collapse;
}

.produits-table th {
  background: var(--gray-50);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.produits-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.produits-table th.sortable:hover {
  background: var(--gray-100);
}

.produits-table th i {
  margin-left: 0.5rem;
  opacity: 0.5;
}

.produits-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  vertical-align: middle;
}

.produit-image-small {
  width: 50px;
  height: 50px;
  border-radius: 4px;
  overflow: hidden;
}

.produit-image-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image-small {
  width: 100%;
  height: 100%;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.produit-info .produit-name {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.produit-description-short {
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.category-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.category-badge.informatique {
  background: var(--primary-light);
  color: var(--primary-color);
}

.category-badge.bureautique {
  background: var(--success-light);
  color: var(--success-color);
}

.category-badge.mobilier {
  background: var(--warning-light);
  color: var(--warning-color);
}

.category-badge.accessoires {
  background: var(--info-light);
  color: var(--info-color);
}

.category-badge.consommables {
  background: var(--gray-100);
  color: var(--text-secondary);
}

.stock-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 80px;
}

.stock-value {
  font-weight: 500;
}

.stock-value.in-stock {
  color: var(--success-color);
}

.stock-value.low-stock {
  color: var(--warning-color);
}

.stock-value.out-of-stock {
  color: var(--danger-color);
}

.stock-bar {
  width: 100%;
  height: 4px;
  background: var(--gray-200);
  border-radius: 2px;
  overflow: hidden;
}

.stock-fill {
  height: 100%;
  transition: width 0.3s;
}

.stock-fill.in-stock {
  background: var(--success-color);
}

.stock-fill.low-stock {
  background: var(--warning-color);
}

.stock-fill.out-of-stock {
  background: var(--danger-color);
}

.supplier-name {
  font-weight: 500;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.status-badge.active {
  background: var(--success-light);
  color: var(--success-color);
}

.status-badge.inactive {
  background: var(--gray-100);
  color: var(--text-secondary);
}

.status-badge.discontinued {
  background: var(--danger-light);
  color: var(--danger-color);
}

.actions-menu {
  display: flex;
  gap: 0.25rem;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.pagination-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page-info {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.produit-details-grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
}

.produit-image-large {
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--gray-100);
}

.produit-image-large img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image-large {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  gap: 1rem;
}

.no-image-large i {
  font-size: 3rem;
}

.produit-info-details h2 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.detail-section {
  margin-bottom: 2rem;
}

.detail-section h4 {
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-weight: 600;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-item label {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.price-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
}

.margin-value {
  font-weight: 500;
  color: var(--success-color);
}

.specifications {
  background: var(--gray-50);
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.text-warning {
  color: var(--warning-color);
}

@media (max-width: 768px) {
  .produits-page {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-row {
    flex-direction: column;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .search-input input {
    width: 100%;
  }
  
  .view-controls {
    flex-direction: column;
    gap: 1rem;
  }
  
  .produits-grid {
    grid-template-columns: 1fr;
  }
  
  .produit-details-grid {
    grid-template-columns: 1fr;
  }
  
  .produit-image-large {
    height: 200px;
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .produits-table-container {
    overflow-x: auto;
  }
  
  .produits-table {
    min-width: 800px;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: var(--text-secondary);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--gray-200);
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Variables CSS — portées par la racine du composant (:root ne matche rien en style scoped) */
.produits-page {
  --primary-color: #667eea; /* violet de l'app */
  --primary-light: #e9ecfc;
  --success-color: #10b981;
  --success-light: #d1fae5;
  --warning-color: #f59e0b;
  --warning-light: #fef3c7;
  --danger-color: #ef4444;
  --danger-light: #fee2e2;
  --info-color: #06b6d4;
  --info-light: #cffafe;
  --text-primary: #1f2937;
  --text-secondary: #6b7280;
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --border-color: #e5e7eb;
}

/* Styles pour les boutons */
.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 0.875rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-secondary {
  background: var(--gray-100);
  color: var(--text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--gray-200);
}

.btn-outline {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.btn-outline:hover:not(:disabled) {
  background: var(--gray-50);
  color: var(--text-primary);
}

.btn-danger {
  background: var(--danger-color);
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background: #dc2626;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.75rem;
}

/* Styles pour les formulaires */
input, select, textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s;
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

@media (max-width: 640px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* Styles pour les modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
}

.modal-small {
  max-width: 400px;
}

.modal-large {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background: var(--gray-100);
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
}

/* ---- Responsive refinements ---- */
.produit-name,
.produit-reference,
.produit-info-details h2,
.detail-item span {
  overflow-wrap: anywhere;
}

.produit-content,
.produit-info,
.produit-info-details,
.filter-group {
  min-width: 0;
}

@media (max-width: 991.98px) {
  .header-actions {
    flex-wrap: wrap;
  }

  .search-input input {
    width: 100%;
  }
}

@media (max-width: 767.98px) {
  .page-title {
    font-size: 1.5rem;
    margin: 0;
  }

  .header-actions .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .filters-section {
    padding: 1.25rem;
  }

  .filters-row {
    align-items: stretch;
  }

  .filter-group,
  .filters-row > .btn {
    width: 100%;
  }

  .filters-row > .btn {
    justify-content: center;
  }

  .view-controls {
    align-items: stretch;
  }

  .view-toggle .btn {
    flex: 1;
    justify-content: center;
  }

  .sort-controls select {
    flex: 1;
    min-width: 0;
  }

  .pagination-container {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }

  .pagination-controls {
    flex-wrap: wrap;
    justify-content: center;
  }

  /* Full-screen modals on phones */
  .modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .modal-content,
  .modal-small,
  .modal-large {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header,
  .modal-footer {
    flex-shrink: 0;
    padding: 1rem 1.25rem;
  }

  .modal-header h3 {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem;
    -webkit-overflow-scrolling: touch;
  }

  .modal-footer {
    flex-wrap: wrap;
  }

  .modal-footer .btn {
    flex: 1 1 auto;
    justify-content: center;
  }

  .detail-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  }
}

@media (max-width: 575.98px) {
  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
    gap: 0.75rem;
    min-width: 0;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.125rem;
    flex-shrink: 0;
  }

  .produit-image {
    height: 160px;
  }
}

@media (pointer: coarse) {
  .produit-actions .btn,
  .actions-menu .btn,
  .pagination-controls .btn,
  .sort-controls .btn,
  .modal-close {
    min-width: 44px;
    min-height: 44px;
    justify-content: center;
  }
}
</style>